import { PurchaseOrderReceiptExceptionStatus } from '@supply-mover-2-0/shared';

/**
 * Pure shared rules for the receipt-exception state machine. Lives
 * in libs/core so both the backend service (apps/crm-api ...
 * exceptionTransitions.ts) AND the frontend Issues tab can read the
 * same allowed-transitions table.
 *
 * Backend wraps these with `assertExceptionTransition` (throws
 * BadRequestException — Nest-only). Frontend uses
 * `getAllowedExceptionTransitions` to render only the buttons that
 * will succeed.
 *
 * Allowed transitions (single source of truth):
 *
 *   OPEN ──> VENDOR_NOTIFIED ─┬── CREDIT_PENDING ──── CREDIT_RECEIVED ─┐
 *                              │                                        │
 *                              ├── REPLACEMENT_PENDING ── REPLACEMENT_RECEIVED ─┼──> RESOLVED
 *                              │                                        │
 *                              └── WRITTEN_OFF ─────────────────────────┘
 *      └── direct close (receiver miskeyed) ──> RESOLVED
 *
 * Plus two cross-cutting transitions managers can force from anywhere:
 *   - any open status → RESOLVED (force close)
 *   - any open status → WRITTEN_OFF
 */

const E = PurchaseOrderReceiptExceptionStatus;

export const ALLOWED_EXCEPTION_TRANSITIONS: ReadonlyMap<
	PurchaseOrderReceiptExceptionStatus,
	ReadonlySet<PurchaseOrderReceiptExceptionStatus>
> = new Map([
	[E.OPEN, new Set([E.VENDOR_NOTIFIED, E.RESOLVED, E.WRITTEN_OFF])],
	[
		E.VENDOR_NOTIFIED,
		new Set([
			E.CREDIT_PENDING,
			E.REPLACEMENT_PENDING,
			E.WRITTEN_OFF,
			E.RESOLVED,
		]),
	],
	[E.CREDIT_PENDING, new Set([E.CREDIT_RECEIVED, E.WRITTEN_OFF, E.RESOLVED])],
	[E.CREDIT_RECEIVED, new Set([E.RESOLVED, E.WRITTEN_OFF])],
	[
		E.REPLACEMENT_PENDING,
		new Set([E.REPLACEMENT_RECEIVED, E.WRITTEN_OFF, E.RESOLVED]),
	],
	[E.REPLACEMENT_RECEIVED, new Set([E.RESOLVED, E.WRITTEN_OFF])],
	// Terminal states — empty allowed set.
	[E.RESOLVED, new Set()],
	[E.WRITTEN_OFF, new Set()],
]);

const TERMINAL_EXCEPTION_STATUSES: ReadonlySet<PurchaseOrderReceiptExceptionStatus> =
	new Set([E.RESOLVED, E.WRITTEN_OFF]);

export function isExceptionTransitionAllowed(
	from: PurchaseOrderReceiptExceptionStatus | string,
	to: PurchaseOrderReceiptExceptionStatus | string,
): boolean {
	const allowed = ALLOWED_EXCEPTION_TRANSITIONS.get(
		from as PurchaseOrderReceiptExceptionStatus,
	);
	if (allowed == null) {
		return false;
	}
	return allowed.has(to as PurchaseOrderReceiptExceptionStatus);
}

export function isExceptionStatusTerminal(
	status: PurchaseOrderReceiptExceptionStatus | string,
): boolean {
	return TERMINAL_EXCEPTION_STATUSES.has(
		status as PurchaseOrderReceiptExceptionStatus,
	);
}

/**
 * Returns the set of statuses an exception in the given status can
 * legally transition to. Used by the UI to render only the buttons
 * that will succeed (vs greying out / hiding others).
 */
export function getAllowedExceptionTransitions(
	from: PurchaseOrderReceiptExceptionStatus | string,
): ReadonlyArray<PurchaseOrderReceiptExceptionStatus> {
	const allowed = ALLOWED_EXCEPTION_TRANSITIONS.get(
		from as PurchaseOrderReceiptExceptionStatus,
	);
	if (allowed == null) {
		return [];
	}
	return Array.from(allowed);
}
